import { useEffect, useRef, useState } from "react";
import { Camera, LogOut, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, PageHeader, Reveal } from "@/components/common";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { MissedDayPolicySection, RemindersSection } from "@/components/RemindersSection";
import { BasicsSection, FoodSection, GoalsSection, ModulesSection, NotesSection, toPayload } from "@/components/profile/ProfileSections";

const Section = ({ title, children, delay }) => (
  <Reveal delay={delay} className="card-clay p-5 sm:p-7">
    <h2 className="mb-5 text-lg font-bold">{title}</h2>
    {children}
  </Reveal>
);

const AvatarSection = () => {
  const { me, refreshMe } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/profile/avatar", fd);
      await refreshMe();
      toast.success("Foto actualizada");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete("/profile/avatar"); await refreshMe(); toast.success("Foto eliminada"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-4" data-testid="profile-avatar-section">
      <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-ink text-2xl font-bold text-white">
        {me.avatar_url ? <img src={`${me.avatar_url}?t=${Date.now()}`} alt="" className="h-full w-full object-cover" /> : (me.name || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="flex flex-wrap gap-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="profile-avatar-input" />
        <button type="button" disabled={busy} onClick={() => inputRef.current?.click()} className="flex items-center gap-2 rounded-full bg-ink px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-testid="profile-avatar-upload-btn">
          <Camera className="h-4 w-4" /> {busy ? "Subiendo…" : "Subir foto"}
        </button>
        {me.avatar_url && (
          <button type="button" disabled={busy} onClick={remove} className="flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="profile-avatar-remove-btn">
            <Trash2 className="h-4 w-4" /> Quitar
          </button>
        )}
      </div>
      <p className="w-full text-xs text-stone-500">JPG o PNG, máximo 2 MB.</p>
    </div>
  );
};

const PasswordSection = () => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", f);
      setF({ current_password: "", new_password: "" });
      toast.success("Contraseña actualizada");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="profile-password-section">
      <Input type="password" placeholder="Contraseña actual" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className="h-11 rounded-2xl" data-testid="profile-password-current" />
      <Input type="password" minLength={6} placeholder="Nueva (mín. 6)" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="h-11 rounded-2xl" data-testid="profile-password-new" />
      <button type="button" disabled={busy || !f.current_password || f.new_password.length < 6} onClick={save} className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60 sm:col-span-2 sm:w-fit" data-testid="profile-password-save-btn">
        {busy ? "Guardando…" : "Cambiar contraseña"}
      </button>
    </div>
  );
};

export default function Profile() {
  const { me, refreshMe, logout } = useAuth();
  const [f, setF] = useState(null);
  const [options, setOptions] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/profile").then((r) => {
      const { targets, age, user_id, onboarding_complete, created_at, updated_at, ...rest } = r.data;
      setF({ ...rest, specific_goals: rest.specific_goals || [] });
    }).catch(() => setF({ modules: me.modules || { training: true, nutrition: true, ailments: false }, specific_goals: [] }));
    api.get("/options").then((r) => setOptions(r.data));
  }, [me.modules]);
  if (!f || !options) return <Loader />;
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const hasFullProfile = Boolean(f.name && f.birth_date);

  const saveModules = async (m) => {
    if (!Object.values(m).some(Boolean)) return toast.error("Activa al menos un módulo");
    set({ modules: m });
    try { await api.patch("/profile/modules", m); await refreshMe(); toast.success("Módulos actualizados"); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    setBusy(true);
    try { await api.put("/profile", toPayload(f)); await refreshMe(); toast.success("Perfil guardado. Tu plan se ha recalculado."); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 pb-20" data-testid="profile-page">
      <PageHeader eyebrow="Perfil" title={`Hola, ${me.name}`} subtitle="Cambia tus datos o tus módulos cuando quieras: los planes se recalculan solos." />
      <Section title="Foto de perfil"><AvatarSection /></Section>
      <Section title="Contraseña" delay={0.03}><PasswordSection /></Section>
      {hasFullProfile ? (
        <>
          {f.modules && <Section title="Mis módulos"><ModulesSection modules={f.modules} onChange={saveModules} /></Section>}
          <Section title="Datos básicos" delay={0.05}><BasicsSection f={f} set={set} /></Section>
          <Section title="Objetivos y entrenamiento" delay={0.1}><GoalsSection f={f} set={set} options={options} /></Section>
          <Section title="Alimentación" delay={0.15}><FoodSection f={f} set={set} options={options} /></Section>
          <Section title="Salud" delay={0.2}><NotesSection f={f} set={set} /></Section>
          <Section title="Si no lo marco" delay={0.22}><MissedDayPolicySection /></Section>
          <Section title="Recordatorios" delay={0.25}><RemindersSection /></Section>
          <Section title="Conexiones" delay={0.3}>
            <div className="flex flex-wrap gap-2">
              <Link to="/actividad" data-testid="profile-activity-link" className="rounded-full bg-train-soft px-4 py-2.5 text-sm font-semibold text-train-ink">Importar actividad</Link>
              {me.role === "admin" && <Link to="/admin" data-testid="profile-admin-link" className="rounded-full bg-ink px-4 py-2.5 text-sm font-semibold text-white">Conectores IA y Strava (admin)</Link>}
            </div>
          </Section>
          <div className="sticky bottom-24 z-20 flex gap-3 lg:bottom-6">
            <button disabled={busy} onClick={save} data-testid="profile-save-btn" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-ink py-3.5 font-semibold text-white shadow-lg transition-transform active:scale-95 disabled:opacity-60">
              <Save className="h-4 w-4" /> {busy ? "Guardando…" : "Guardar cambios"}
            </button>
            <button onClick={logout} data-testid="profile-logout-btn" className="flex items-center gap-2 rounded-full border border-stone-300 bg-white px-5 font-semibold transition-transform active:scale-95 lg:hidden">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </>
      ) : (
        <Section title="Cuenta">
          <p className="text-sm text-stone-600">Esta cuenta aún no tiene cuestionario de perfil. {me.role === "admin" ? "Como admin puedes gestionar conectores sin completarlo." : <Link to="/bienvenida" className="font-semibold underline">Completar perfil</Link>}</p>
          {me.role === "admin" && <Link to="/admin" className="mt-3 inline-flex rounded-full bg-ink px-4 py-2.5 text-sm font-semibold text-white">Ir a Conectores</Link>}
        </Section>
      )}
    </div>
  );
}
